// src/api/crm.ts
// Frontend API client for CRM module (Aligned with SRS Addendum)
import { fetchJson, fetchBlob } from './apiClient';
import type {
  Organization, CrmTeamMember, CrmLead, LeadActivity,
  Campaign, CrmDashboardData, TeamPerformance, PipelineStage,
  CrmNotification, CampaignAnalytics, ActivityKPI,
  CrmLeadStatus, CrmLeadSource, ActivityType, CampaignStatus, SourceChannel,
} from '../types/crm';
import type { CrmPlanOption, CrmPlanId } from '../config/crmPlans';

export type { CrmPlanOption, CrmPlanId };

const CRM_PLAN_IDS = new Set<CrmPlanId>(['crm_small', 'crm_medium', 'crm_large', 'crm_enterprise']);

function normalizeCrmPlan(raw: any): CrmPlanOption | null {
  const planId = String(raw?.planId || raw?.id || '').trim() as CrmPlanId;
  if (!CRM_PLAN_IDS.has(planId)) return null;
  return {
    id: planId,
    planId,
    name: String(raw?.name || planId),
    price: String(raw?.price || ''),
    priceUsd: Number(raw?.priceUsd || 0),
    seatLimit: raw?.seatLimit == null ? null : Number(raw.seatLimit),
    description: String(raw?.description || ''),
    features: Array.isArray(raw?.features) ? raw.features.map(String) : [],
  };
}

// ===== Organization =====

export async function createOrUpdateOrganization(data: {
  name: string; industry?: string; email?: string; phone?: string; website?: string; address?: string; crmCategory?: string;
}): Promise<{ ok: boolean; organization: Organization }> {
  return fetchJson('/api/crm/organization', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/** Pilot/test: switch org category and sync FO module defaults (client only). */
export async function setOrganizationCrmCategory(data: {
  crmCategory: string;
  companyName: string;
}): Promise<{ ok: boolean; organization: Organization; crmCategory: string }> {
  return fetchJson('/api/crm/organization/crm-category', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function getOrganization(): Promise<Organization> {
  return fetchJson('/api/crm/organization');
}

export interface CrmScoreWeights {
  closedWon: number;
  qualifiedLeads: number;
  customerVisits: number;
  routeCompletion: number;
  followUpsOnTime: number;
  campaignContribution: number;
  attendanceCompliance: number;
}

export interface CrmScoreConfig {
  enabled: boolean;
  weights: CrmScoreWeights;
  minSignals: number;
  repsCanSeeLeaderboard: boolean;
  managersCanAdjustTargets: boolean;
  defaults?: CrmScoreWeights;
}

export async function getCrmScoreSettings(): Promise<{ ok: boolean; config: CrmScoreConfig }> {
  return fetchJson('/api/crm/score-settings');
}

export async function updateCrmScoreSettings(payload: Partial<CrmScoreConfig>): Promise<{ ok: boolean; config: CrmScoreConfig }> {
  return fetchJson('/api/crm/score-settings', {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export type LocationTrackingSchedule = {
  weekdayStart: string;
  weekdayEnd: string;
  saturdayEnabled: boolean;
  saturdayStart: string;
  saturdayEnd: string;
  sundayEnabled: boolean;
  sundayStart: string;
  sundayEnd: string;
};

export async function getLocationTrackingSettings(): Promise<{ ok: boolean; schedule: LocationTrackingSchedule }> {
  return fetchJson('/api/crm/location-tracking-settings');
}

export type FieldOpsFlags = {
  fieldOperationsEnabled: boolean;
  sharedWizardEnabled: boolean;
  operationalIssuesEnabled: boolean;
  generalModuleEnabled: boolean;
  cleaningModuleEnabled: boolean;
  securityModuleEnabled: boolean;
  technicalModuleEnabled: boolean;
  advancedTemplatesEnabled: boolean;
  fieldOpsReportsEnabled: boolean;
  fieldOpsScoringEnabled: boolean;
};

export type FieldOpsLocationSchedule = LocationTrackingSchedule;

export async function getFieldOpsModules(): Promise<{
  ok: boolean;
  flags: FieldOpsFlags;
  effective?: FieldOpsFlags;
  fieldOpsPatrolIntervalHours?: number;
  fieldOpsLocationSchedule?: FieldOpsLocationSchedule | null;
  fieldOpsLocationSchedules?: Record<string, FieldOpsLocationSchedule>;
  crmCategory?: string;
}> {
  return fetchJson('/api/crm/field-ops/modules');
}

export async function updateFieldOpsModules(
  flags: Partial<FieldOpsFlags>,
): Promise<{
  ok: boolean;
  flags: FieldOpsFlags;
  fieldOpsPatrolIntervalHours?: number;
  fieldOpsLocationSchedule?: FieldOpsLocationSchedule | null;
}> {
  return fetchJson('/api/crm/field-ops/modules', {
    method: 'PATCH',
    body: JSON.stringify({ flags }),
  });
}

export async function updateFieldOpsSettings(body: {
  flags?: Partial<FieldOpsFlags>;
  fieldOpsPatrolIntervalHours?: number;
  fieldOpsLocationSchedule?: Partial<FieldOpsLocationSchedule>;
  scheduleCategory?: string;
}): Promise<{
  ok: boolean;
  flags: FieldOpsFlags;
  fieldOpsPatrolIntervalHours?: number;
  fieldOpsLocationSchedule?: FieldOpsLocationSchedule | null;
}> {
  return fetchJson('/api/crm/field-ops/modules', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export async function getFieldOpsRoleProfiles(): Promise<{
  ok: boolean;
  fieldRoles: string[];
  profiles: any[];
}> {
  return fetchJson('/api/crm/field-ops/role-profiles');
}

export async function updateUserFieldRole(
  userId: string,
  data: { fieldRole?: string | null; fieldSiteIds?: string[] | null },
): Promise<{ ok: boolean; user: any }> {
  return fetchJson(`/api/crm/field-ops/users/${userId}/field-role`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export type FieldOpsSiteAssignee = {
  id: string;
  name: string;
  email?: string | null;
  fieldRole?: string | null;
  crmRole?: string | null;
};

export type FieldOpsSite = {
  id: string;
  name: string;
  clientName?: string | null;
  address?: string | null;
  latitude: number;
  longitude: number;
  radiusM: number;
  status: string;
  assignees?: FieldOpsSiteAssignee[];
};

export async function getFieldOpsSites(status: string = 'active'): Promise<{
  ok: boolean;
  sites: FieldOpsSite[];
  assignmentMode?: 'exclusive' | 'multi';
}> {
  return fetchJson(`/api/crm/field-ops/sites?status=${encodeURIComponent(status)}`);
}

export async function createFieldOpsSite(data: {
  name: string;
  clientName?: string;
  address?: string;
  latitude: number;
  longitude: number;
  radiusM?: number;
  assigneeUserId?: string;
  assigneeUserIds?: string[];
}): Promise<{ ok: boolean; site: FieldOpsSite }> {
  return fetchJson('/api/crm/field-ops/sites', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateFieldOpsSite(
  id: string,
  data: Partial<{
    name: string;
    clientName: string;
    address: string;
    latitude: number;
    longitude: number;
    radiusM: number;
    status: string;
  }>,
): Promise<{ ok: boolean; site: FieldOpsSite }> {
  return fetchJson(`/api/crm/field-ops/sites/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteFieldOpsSite(id: string): Promise<{ ok: boolean }> {
  return fetchJson(`/api/crm/field-ops/sites/${id}`, { method: 'DELETE' });
}

export async function setFieldOpsSiteAssignees(
  siteId: string,
  userIds: string[],
): Promise<{
  ok: boolean;
  assignees: FieldOpsSiteAssignee[];
  assignmentMode?: 'exclusive' | 'multi';
}> {
  return fetchJson(`/api/crm/field-ops/sites/${encodeURIComponent(siteId)}/assignees`, {
    method: 'PUT',
    body: JSON.stringify({ userIds }),
  });
}

export type FieldOpsAssignment = {
  id: string;
  type: string;
  title: string;
  siteId: string | null;
  assignedUserId: string | null;
  status: string;
  startAt?: string | null;
  endAt?: string | null;
};

export type FieldOpsPatrolCheckpoint = {
  id: string;
  siteId: string;
  label: string;
  sequence: number;
  latitude: number;
  longitude: number;
  radiusM: number;
};

export async function getFieldOpsPatrolCheckpoints(
  siteId: string,
): Promise<{ ok: boolean; checkpoints: FieldOpsPatrolCheckpoint[] }> {
  return fetchJson(`/api/crm/field-ops/sites/${siteId}/patrol-checkpoints`);
}

export async function saveFieldOpsPatrolCheckpoints(
  siteId: string,
  checkpoints: Array<{
    label: string;
    latitude: number;
    longitude: number;
    radiusM?: number;
  }>,
): Promise<{ ok: boolean; checkpoints: FieldOpsPatrolCheckpoint[] }> {
  return fetchJson(`/api/crm/field-ops/sites/${siteId}/patrol-checkpoints`, {
    method: 'PUT',
    body: JSON.stringify({ checkpoints }),
  });
}

export type FieldOpsEmergencyAlert = {
  id: string;
  userId: string;
  siteId?: string | null;
  status: string;
  activatedAt: string;
  latitude?: number | null;
  longitude?: number | null;
  note?: string | null;
  history?: any[];
};

export async function getFieldOpsEmergencyAlerts(
  status?: string,
): Promise<{ ok: boolean; alerts: FieldOpsEmergencyAlert[] }> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  return fetchJson(`/api/crm/field-ops/emergency-alerts${qs}`);
}

export async function applyFieldOpsEmergencyAlertAction(
  alertId: string,
  data: { actionType: string; note?: string },
): Promise<{ ok: boolean; alert: FieldOpsEmergencyAlert }> {
  return fetchJson(`/api/crm/field-ops/emergency-alerts/${alertId}/actions`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getFieldOpsAssignments(): Promise<{ ok: boolean; assignments: FieldOpsAssignment[] }> {
  return fetchJson('/api/crm/field-ops/assignments');
}

export async function createFieldOpsAssignment(data: {
  title: string;
  type?: string;
  siteId?: string;
  assignedUserId?: string;
  status?: string;
  startAt?: string;
  endAt?: string;
}): Promise<{ ok: boolean; assignment: FieldOpsAssignment }> {
  return fetchJson('/api/crm/field-ops/assignments', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function startFieldOpsWork(data?: {
  siteId?: string;
  assignmentId?: string;
  clientEventId?: string;
}): Promise<{ ok: boolean; session: any }> {
  return fetchJson('/api/crm/field-ops/tracking-sessions/start-work', {
    method: 'POST',
    body: JSON.stringify(data || {}),
  });
}

export async function endFieldOpsWork(reason?: string): Promise<{ ok: boolean; session: any }> {
  return fetchJson('/api/crm/field-ops/tracking-sessions/end-work', {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export async function getActiveFieldOpsSession(): Promise<{ ok: boolean; session: any }> {
  return fetchJson('/api/crm/field-ops/tracking-sessions/active');
}

export type FoFieldRouteStop = {
  stopId?: string;
  name: string;
  address?: string | null;
  latitude: number;
  longitude: number;
  radiusM?: number;
  sequence?: number;
};

export type FoFieldRoute = {
  id: string;
  organizationId?: string;
  title: string;
  routeDate: string;
  assignedUserId: string;
  moduleHint?: string;
  status: string;
  scheduleKind?: string;
  stops: FoFieldRouteStop[];
  publishedAt?: string | null;
  createdBy?: string | null;
  softDeletedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

export type FoOffSiteStart = {
  id: string;
  userId: string;
  siteId?: string | null;
  startedAt?: string;
  startLatitude?: number | null;
  startLongitude?: number | null;
  startOffSite?: boolean | null;
  startDistanceM?: number | null;
  startPlaceName?: string | null;
  workerName?: string | null;
  workerEmail?: string | null;
  siteName?: string | null;
  status?: string;
};

export async function listFoFieldRoutes(params?: {
  from?: string;
  to?: string;
  workerId?: string;
  status?: string;
}): Promise<{ ok: boolean; routes: FoFieldRoute[] }> {
  const q = new URLSearchParams();
  if (params?.from) q.set('from', params.from);
  if (params?.to) q.set('to', params.to);
  if (params?.workerId) q.set('workerId', params.workerId);
  if (params?.status) q.set('status', params.status);
  const qs = q.toString();
  return fetchJson(`/api/crm/field-ops/field-routes${qs ? `?${qs}` : ''}`);
}

export async function getFoFieldRoute(
  id: string,
): Promise<{ ok: boolean; route: FoFieldRoute; visits?: any[] }> {
  return fetchJson(`/api/crm/field-ops/field-routes/${id}`);
}

export async function createFoFieldRoute(data: {
  title: string;
  routeDate: string;
  assignedUserId: string;
  moduleHint?: string;
  scheduleKind?: string;
  stops: FoFieldRouteStop[];
}): Promise<{ ok: boolean; route: FoFieldRoute }> {
  return fetchJson('/api/crm/field-ops/field-routes', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateFoFieldRoute(
  id: string,
  data: Partial<{
    title: string;
    routeDate: string;
    assignedUserId: string;
    moduleHint: string;
    scheduleKind: string;
    stops: FoFieldRouteStop[];
    status: string;
  }>,
): Promise<{ ok: boolean; route: FoFieldRoute }> {
  return fetchJson(`/api/crm/field-ops/field-routes/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function publishFoFieldRoute(
  id: string,
): Promise<{ ok: boolean; route: FoFieldRoute; copies?: FoFieldRoute[] }> {
  return fetchJson(`/api/crm/field-ops/field-routes/${id}/publish`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function deleteFoFieldRoute(id: string): Promise<{ ok: boolean; deleted?: boolean }> {
  return fetchJson(`/api/crm/field-ops/field-routes/${id}`, { method: 'DELETE' });
}

export async function getFoOffSiteStarts(params?: {
  from?: string;
  to?: string;
}): Promise<{ ok: boolean; sessions: FoOffSiteStart[] }> {
  const q = new URLSearchParams();
  if (params?.from) q.set('from', params.from);
  if (params?.to) q.set('to', params.to);
  const qs = q.toString();
  return fetchJson(`/api/crm/field-ops/tracking-sessions/off-site-starts${qs ? `?${qs}` : ''}`);
}

export async function getFieldOpsWizardTemplates(): Promise<{ ok: boolean; templates: any[] }> {
  return fetchJson('/api/crm/field-ops/wizard-templates');
}

export async function ensureFieldOpsGeneralWizard(): Promise<{ ok: boolean; template: any; draft: any }> {
  return fetchJson('/api/crm/field-ops/wizard-templates/ensure-general', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function ensureFieldOpsCleaningWizard(): Promise<{ ok: boolean; template: any; draft: any }> {
  return fetchJson('/api/crm/field-ops/wizard-templates/ensure-cleaning', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function ensureFieldOpsSecurityWizard(): Promise<{ ok: boolean; template: any; draft: any }> {
  return fetchJson('/api/crm/field-ops/wizard-templates/ensure-security', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function ensureFieldOpsTechnicalWizard(): Promise<{ ok: boolean; template: any; draft: any }> {
  return fetchJson('/api/crm/field-ops/wizard-templates/ensure-technical', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function publishFieldOpsWizardTemplate(
  id: string,
): Promise<{ ok: boolean; template: any; version: any }> {
  return fetchJson(`/api/crm/field-ops/wizard-templates/${id}/publish`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function getFieldOpsWizardDraft(
  id: string,
): Promise<{ ok: boolean; version: any }> {
  return fetchJson(`/api/crm/field-ops/wizard-templates/${id}/versions/draft`);
}

export async function patchFieldOpsWizardDraft(
  id: string,
  questions: any[],
): Promise<{ ok: boolean; version: any }> {
  return fetchJson(`/api/crm/field-ops/wizard-templates/${id}/versions/draft`, {
    method: 'PATCH',
    body: JSON.stringify({ questions }),
  });
}

export type FieldOpsReportFact = { code: string; label: string; severity?: string };

/** §16 comprehensive FO report (exceptions, modules, performance, support). */
export type FieldOpsExceptionManagerAction = {
  id: string;
  actorId?: string;
  actorName?: string | null;
  targetId: string;
  tileKey: string;
  siteId?: string | null;
  siteName?: string | null;
  title?: string | null;
  disposition: string;
  note?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

export type FieldOpsReportPayload = {
  range: { from: string; to: string };
  filters: { siteId?: string; workerId?: string; moduleHint?: string };
  kpis: {
    checkIns: number;
    checkOuts: number;
    assignmentsTotal: number;
    assignmentsCompleted: number;
    assignmentsOpen: number;
    openIssues: number;
    urgentIssues: number;
  };
  attendance: Array<{
    workerId: string;
    workerName: string;
    checkIns: number;
    checkOuts: number;
  }>;
  sites: Array<{
    siteId: string;
    siteName: string;
    openIssues: number;
    assignmentsCompleted: number;
    assignmentsOpen: number;
  }>;
  issues: Array<{
    id: string;
    title: string;
    severity: string;
    status: string;
    siteId?: string;
    createdAt?: string;
  }>;
  exceptionSnapshot?: {
    cards: FieldOpsExceptionCards;
    orgStatus: string;
    orgFacts: FieldOpsReportFact[];
    sitesNeedingAttention: Array<{
      siteId: string;
      siteName: string;
      status: string;
      facts: FieldOpsReportFact[];
    }>;
    generatedAt: string;
  };
  managerActions?: FieldOpsExceptionManagerAction[];
  supportRequests?: Array<{
    id: string;
    requestType: string;
    urgency: string;
    status: string;
    title: string;
    siteId?: string;
    siteName?: string;
    createdAt?: string;
    resolvedAt?: string;
  }>;
  cleaning?: {
    signals: {
      understaffedSites: number;
      materialsShortage: number;
      lowCustomerRating: number;
    };
    siteDetails: Array<{
      siteId: string;
      siteName: string;
      facts: FieldOpsReportFact[];
      submittedAt?: string;
    }>;
  };
  security?: {
    signals: {
      missedPatrolSites: number;
      activeEmergencyAlerts: number;
      securityIncidents: number;
    };
    patrols: Array<{
      siteId: string;
      siteName: string;
      checkpointsTotal: number;
      checked: number;
      missed: number;
    }>;
    emergencies: Array<{
      id: string;
      status: string;
      siteId?: string;
      siteName?: string;
      activatedAt?: string;
      resolvedAt?: string;
    }>;
    siteIncidents: Array<{
      siteId: string;
      siteName: string;
      facts: FieldOpsReportFact[];
      submittedAt?: string;
    }>;
  };
  technical?: {
    signals: {
      openFaultSites: number;
      partsNeededSites: number;
      followUpSites: number;
    };
    siteDetails: Array<{
      siteId: string;
      siteName: string;
      facts: FieldOpsReportFact[];
      submittedAt?: string;
    }>;
    partsNeeded?: Array<{
      id: string;
      siteId: string;
      siteName: string;
      workerId?: string;
      workerName?: string;
      items: string[];
      otherText?: string;
      waitingParts: boolean;
      repairStatus?: string;
      submittedAt?: string;
    }>;
  };
  general?: {
    signals: {
      understaffedSites: number;
      siteConditionIssues: number;
      unresolvedProblems: number;
      lowClientSatisfaction: number;
      needsSupportSites: number;
    };
    siteDetails: Array<{
      siteId: string;
      siteName: string;
      facts: FieldOpsReportFact[];
      submittedAt?: string;
    }>;
  };
  performance?: {
    enabled: boolean;
    workers: Array<{
      workerId: string;
      workerName: string;
      score: number | null;
      signals: number;
    }>;
  };
  performanceExtras?: {
    completionRate: number;
    workersReporting: number;
    sitesActive: number;
  };
  siteCompliance?: Array<{
    siteId: string;
    siteName: string;
    status: string;
    facts: FieldOpsReportFact[];
    assignmentsCompleted: number;
    assignmentsOpen: number;
    openIssues: number;
  }>;
  foRouteCompliance?: {
    routesPublished: number;
    stopsPlanned: number;
    stopsCheckedIn: number;
    stopsCheckedOut: number;
    stopsMissed: number;
    stopsPending: number;
  };
};

export async function getFieldOpsReport(params: {
  from: string;
  to: string;
  siteId?: string;
  workerId?: string;
  moduleHint?: string;
}): Promise<{ ok: boolean; report: FieldOpsReportPayload }> {
  const q = new URLSearchParams();
  q.set('from', params.from);
  q.set('to', params.to);
  if (params.siteId) q.set('siteId', params.siteId);
  if (params.workerId) q.set('workerId', params.workerId);
  if (params.moduleHint) q.set('moduleHint', params.moduleHint);
  return fetchJson(`/api/crm/field-ops/reports?${q.toString()}`);
}

export async function downloadFieldOpsReportExcel(params: {
  from: string;
  to: string;
  siteId?: string;
  workerId?: string;
}): Promise<Blob> {
  const q = new URLSearchParams({ from: params.from, to: params.to });
  if (params.siteId) q.set('siteId', params.siteId);
  if (params.workerId) q.set('workerId', params.workerId);
  return fetchBlob(`/api/crm/field-ops/reports/export.xlsx?${q.toString()}`);
}

export async function downloadFieldOpsReportPdf(params: {
  from: string;
  to: string;
  siteId?: string;
  workerId?: string;
  /** UI language: en | sw | fr — PDF labels follow this */
  lang?: string;
}): Promise<Blob> {
  const q = new URLSearchParams({ from: params.from, to: params.to });
  if (params.siteId) q.set('siteId', params.siteId);
  if (params.workerId) q.set('workerId', params.workerId);
  if (params.lang) q.set('lang', params.lang);
  return fetchBlob(`/api/crm/field-ops/reports/export.pdf?${q.toString()}`);
}

export type FieldOpsScoreWeights = {
  presence: number;
  onTime: number;
  assignmentCompletion: number;
  issueHygiene: number;
  moduleCompliance: number;
};

export type FieldOpsScoreConfig = {
  enabled: boolean;
  weights: FieldOpsScoreWeights;
  minSignals: number;
  defaults?: FieldOpsScoreWeights;
};

export async function getFieldOpsScoreSettings(): Promise<{
  ok: boolean;
  config: FieldOpsScoreConfig;
}> {
  return fetchJson('/api/crm/field-ops/score-settings');
}

export async function updateFieldOpsScoreSettings(
  config: Partial<FieldOpsScoreConfig>,
): Promise<{ ok: boolean; config: FieldOpsScoreConfig }> {
  return fetchJson('/api/crm/field-ops/score-settings', {
    method: 'PATCH',
    body: JSON.stringify({ config }),
  });
}

export async function getFieldOpsScoreboard(params: {
  from: string;
  to: string;
}): Promise<{
  ok: boolean;
  range: { from: string; to: string };
  workers: Array<{
    workerId: string;
    workerName: string;
    score: number | null;
    signals: number;
  }>;
}> {
  const q = new URLSearchParams({ from: params.from, to: params.to });
  return fetchJson(`/api/crm/field-ops/scoreboard?${q.toString()}`);
}

export async function snapshotFieldOpsScoreboard(params: {
  from: string;
  to: string;
}): Promise<{ ok: boolean }> {
  return fetchJson('/api/crm/field-ops/scoreboard/snapshot', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export async function getActiveFieldOpsWizard(): Promise<{
  ok: boolean;
  template: any;
  version: any;
  message?: string;
}> {
  return fetchJson('/api/crm/field-ops/wizard-templates/active');
}

export async function submitFieldOpsWizard(data: {
  templateVersionId: string;
  clientEventId: string;
  answers: any[];
  assignmentId?: string;
  siteId?: string;
  trackingSessionId?: string;
  attachmentIds?: string[];
  deviceSubmittedAt?: string;
  lang?: string;
}): Promise<{ ok: boolean; submission?: any; duplicate?: boolean; summaryText?: string }> {
  return fetchJson('/api/crm/field-ops/wizard-submissions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export type FieldOpsWizardSubmission = {
  id: string;
  userId: string;
  workerName: string;
  siteId?: string | null;
  siteName?: string | null;
  templateVersion?: number;
  summaryText?: string | null;
  answers?: Array<{ questionId: string; type?: string; value?: any; otherText?: string }>;
  clientEventId?: string;
  serverReceivedAt?: string;
  createdAt?: string;
};

export async function getFieldOpsWizardSubmissions(): Promise<{
  ok: boolean;
  submissions: FieldOpsWizardSubmission[];
}> {
  return fetchJson('/api/crm/field-ops/wizard-submissions');
}

// ===== Field Ops Issues + Support (Phase 1.4) =====

export type FieldOpsIssue = {
  id: string;
  category: string;
  severity: string;
  status: string;
  title: string;
  description?: string | null;
  reporterId: string;
  assigneeId?: string | null;
  siteId?: string | null;
  createdAt?: string;
};

export type FieldOpsSupportRequest = {
  id: string;
  requestType: string;
  urgency: string;
  status: string;
  title: string;
  description?: string | null;
  items?: string | null;
  quantity?: number | null;
  requesterId: string;
  createdAt?: string;
};

export async function getFieldOpsIssues(status?: string): Promise<{ ok: boolean; issues: FieldOpsIssue[] }> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  return fetchJson(`/api/crm/field-ops/issues${qs}`);
}

export async function createFieldOpsIssue(data: {
  category: string;
  severity?: string;
  title: string;
  description?: string;
  siteId?: string;
  assignmentId?: string;
  clientEventId: string;
}): Promise<{ ok: boolean; issue: FieldOpsIssue; duplicate?: boolean }> {
  return fetchJson('/api/crm/field-ops/issues', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function applyFieldOpsIssueAction(
  issueId: string,
  data: { actionType: string; note?: string; assigneeId?: string },
): Promise<{ ok: boolean; issue: FieldOpsIssue; action: any }> {
  return fetchJson(`/api/crm/field-ops/issues/${issueId}/actions`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getFieldOpsSupportRequests(
  status?: string,
): Promise<{ ok: boolean; requests: FieldOpsSupportRequest[] }> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  return fetchJson(`/api/crm/field-ops/support-requests${qs}`);
}

export async function createFieldOpsSupportRequest(data: {
  requestType: string;
  urgency?: string;
  title: string;
  description?: string;
  items?: string;
  quantity?: number;
  siteId?: string;
  clientEventId: string;
}): Promise<{ ok: boolean; request: FieldOpsSupportRequest; duplicate?: boolean }> {
  return fetchJson('/api/crm/field-ops/support-requests', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateFieldOpsSupportRequest(
  id: string,
  data: { status: string },
): Promise<{ ok: boolean; request: FieldOpsSupportRequest }> {
  return fetchJson(`/api/crm/field-ops/support-requests/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

// ===== Field Ops Summaries + Exception Dashboard (Phase 1.5) =====

export type FieldOpsExceptionCards = {
  workersAbsentOrLate: number;
  missedAssignments: number;
  workersOffline: number;
  openIssues: number;
  urgentAlerts: number;
  sitesNeedingAttention: number;
  pendingSupportRequests: number;
  understaffedSites?: number;
  materialsShortage?: number;
  lowCustomerRating?: number;
  missedPatrolSites?: number;
  activeEmergencyAlerts?: number;
  securityIncidents?: number;
  openFaultSites?: number;
  partsNeededSites?: number;
  followUpSites?: number;
  siteConditionIssues?: number;
  unresolvedProblems?: number;
  lowClientSatisfaction?: number;
  needsSupportSites?: number;
};

export type FieldOpsExceptionDetailItem = {
  id?: string;
  title: string;
  subtitle?: string;
  reason?: string;
  /** Full multi-line field report for managers. */
  body?: string;
  reportedAt?: string;
  severity?: string;
  status?: string;
  actioned?: boolean;
  managerResponse?: {
    disposition: string;
    note?: string | null;
    actorId?: string;
    actorName?: string | null;
    at?: string;
  };
  meta?: Record<string, string | number | null | undefined>;
};

export type FieldOpsExceptionDetails = {
  sitesNeedingAttention: FieldOpsExceptionDetailItem[];
  workersOffline: FieldOpsExceptionDetailItem[];
  workersAbsentOrLate: FieldOpsExceptionDetailItem[];
  missedAssignments: FieldOpsExceptionDetailItem[];
  openIssues: FieldOpsExceptionDetailItem[];
  pendingSupportRequests: FieldOpsExceptionDetailItem[];
  urgentAlerts: FieldOpsExceptionDetailItem[];
  activeEmergencyAlerts: FieldOpsExceptionDetailItem[];
  missedPatrolSites: FieldOpsExceptionDetailItem[];
  securityIncidents: FieldOpsExceptionDetailItem[];
  understaffedSites: FieldOpsExceptionDetailItem[];
  materialsShortage: FieldOpsExceptionDetailItem[];
  lowCustomerRating: FieldOpsExceptionDetailItem[];
  openFaultSites: FieldOpsExceptionDetailItem[];
  partsNeededSites: FieldOpsExceptionDetailItem[];
  followUpSites: FieldOpsExceptionDetailItem[];
  siteConditionIssues: FieldOpsExceptionDetailItem[];
  unresolvedProblems: FieldOpsExceptionDetailItem[];
  lowClientSatisfaction: FieldOpsExceptionDetailItem[];
  needsSupportSites: FieldOpsExceptionDetailItem[];
};

export type FieldOpsSummary = {
  id: string;
  scopeType: string;
  scopeId: string;
  status: string;
  facts: Array<{ code: string; label: string; severity?: string }>;
  generatedAt?: string;
  title?: string | null;
};

export async function getFieldOpsExceptionDashboard(): Promise<{
  ok: boolean;
  cards: FieldOpsExceptionCards;
  details: FieldOpsExceptionDetails;
  unreadByTile?: Record<string, number>;
  recentManagerActions?: FieldOpsExceptionManagerAction[];
  orgSummary: FieldOpsSummary | null;
  sitesNeedingAttention: FieldOpsSummary[];
  generatedAt: string;
}> {
  return fetchJson('/api/crm/field-ops/exception-dashboard');
}

export async function postFieldOpsExceptionResponse(data: {
  targetId: string;
  tileKey: string;
  disposition: string;
  note?: string;
  siteId?: string;
  title?: string;
  issueId?: string;
}): Promise<{ ok: boolean; response: FieldOpsExceptionManagerAction }> {
  return fetchJson('/api/crm/field-ops/exception-responses', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getFieldOpsExceptionResponses(params?: {
  from?: string;
  to?: string;
  limit?: number;
}): Promise<{
  ok: boolean;
  dispositions: string[];
  responses: FieldOpsExceptionManagerAction[];
}> {
  const qs = new URLSearchParams();
  if (params?.from) qs.set('from', params.from);
  if (params?.to) qs.set('to', params.to);
  if (params?.limit) qs.set('limit', String(params.limit));
  const q = qs.toString();
  return fetchJson(`/api/crm/field-ops/exception-responses${q ? `?${q}` : ''}`);
}

export async function regenerateFieldOpsSummaries(): Promise<{
  ok: boolean;
  cards: FieldOpsExceptionCards;
  details?: FieldOpsExceptionDetails;
  summaries: FieldOpsSummary[];
}> {
  return fetchJson('/api/crm/field-ops/summaries/regenerate', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function updateLocationTrackingSettings(
  schedule: Partial<LocationTrackingSchedule>,
): Promise<{ ok: boolean; schedule: LocationTrackingSchedule }> {
  return fetchJson('/api/crm/location-tracking-settings', {
    method: 'PATCH',
    body: JSON.stringify({ schedule }),
  });
}

export async function getCrmPlans(): Promise<CrmPlanOption[]> {
  try {
    const data = await fetchJson('/api/crm/public/crm-plans', { auth: false });
    if (!Array.isArray(data)) return [];
    return data.map(normalizeCrmPlan).filter(Boolean) as CrmPlanOption[];
  } catch {
    return [];
  }
}

export interface CrmAccessStatus {
  ok?: boolean;
  hasCrmRole?: boolean;
  crmRole?: string;
  crmActive?: boolean;
  showCrmUi?: boolean;
  canUseCrmFeatures?: boolean;
  pendingPlanId?: string | null;
  organization?: {
    _id: string;
    name?: string;
    crmPlan?: string;
    crmSeatLimit?: number | null;
    crmPendingPlan?: string | null;
    crmSubscriptionActive?: boolean;
  } | null;
}

export async function getCrmAccessStatus(): Promise<CrmAccessStatus> {
  return fetchJson('/api/crm/access-status');
}

export async function initializeCrmSignup(payload: {
  planId: string;
  companyName?: string;
  crmCategory?: string;
}): Promise<{ ok: boolean; organization?: Organization; crmActive?: boolean; crmCategory?: string | null }> {
  return fetchJson('/api/crm/signup/initialize', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// ===== Team Management =====

export async function getTeamMembers(): Promise<{ ok: boolean; members: CrmTeamMember[] }> {
  return fetchJson('/api/crm/team');
}

export async function createManager(data: {
  name: string; email: string; phone?: string; company?: string; position?: string; location?: string; templateId?: string;
  password: string; confirmPassword: string; avatarUrl?: string;
}): Promise<{ ok: boolean; user: CrmTeamMember & { username?: string } }> {
  return fetchJson('/api/crm/team/manager', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function createSalesRep(data: {
  name: string; email: string; phone?: string; company?: string; position?: string; location?: string; templateId?: string; managerId?: string;
  password: string; confirmPassword: string; avatarUrl?: string;
}): Promise<{ ok: boolean; user: CrmTeamMember & { username?: string } }> {
  return fetchJson('/api/crm/team/sales-rep', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateTeamMember(userId: string, data: {
  name?: string; phone?: string; company?: string; position?: string; location?: string; templateId?: string;
}): Promise<{ ok: boolean }> {
  return fetchJson(`/api/crm/team/${userId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function suspendTeamMember(userId: string, suspend: boolean = true): Promise<{ ok: boolean; isSuspended: boolean }> {
  return fetchJson(`/api/crm/team/${userId}/suspend`, {
    method: 'PATCH',
    body: JSON.stringify({ suspend }),
  });
}

export async function deleteTeamMember(userId: string): Promise<{ ok: boolean; deleted?: boolean; pendingApproval?: boolean; message?: string }> {
  return fetchJson(`/api/crm/team/${userId}`, { method: 'DELETE' });
}

export async function approveDeleteMember(userId: string, approve: boolean): Promise<{ ok: boolean; deleted?: boolean }> {
  return fetchJson(`/api/crm/team/${userId}/approve-delete`, {
    method: 'POST',
    body: JSON.stringify({ approve }),
  });
}

export async function getPendingDeletions(): Promise<{ ok: boolean; pending: any[] }> {
  return fetchJson('/api/crm/team/pending-deletions');
}

// ===== CRM Leads =====

export async function createCrmLead(data: {
  name: string; phone?: string; email?: string; company?: string; interest?: string;
  notes?: string; source?: CrmLeadSource; campaignId?: string; tags?: string[];
  deviceId?: string; localCaptureTime?: string;
}): Promise<{ ok: boolean; lead: CrmLead; duplicateWarning?: { isDuplicate: boolean; duplicateFlag: string; existingLeadId: string } }> {
  return fetchJson('/api/crm/leads', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function submitContactExchange(salesRepId: string, data: {
  name: string; phone?: string; email?: string; company?: string; interest?: string; notes?: string;
  campaignId?: string; tcardId?: string; preferredFollowUp?: string; location?: string; consentGiven?: boolean;
}): Promise<{ ok: boolean; message: string }> {
  return fetchJson(`/api/crm/leads/exchange/${salesRepId}`, {
    method: 'POST',
    body: JSON.stringify(data),
    auth: false, // Public endpoint — no auth
  });
}

export async function getCrmLeads(params?: {
  status?: CrmLeadStatus; campaign_id?: string; sales_rep_id?: string;
  source?: CrmLeadSource; page?: number; limit?: number;
  search?: string; date_from?: string; date_to?: string;
  overdue?: boolean; is_duplicate?: boolean;
}): Promise<{ ok: boolean; leads: CrmLead[]; total: number; page: number; limit: number }> {
  const qs = new URLSearchParams();
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null) qs.set(k, String(v));
    }
  }
  const query = qs.toString();
  return fetchJson(`/api/crm/leads${query ? `?${query}` : ''}`);
}

export async function getCrmLead(id: string): Promise<{ ok: boolean; lead: CrmLead; activities: LeadActivity[] }> {
  return fetchJson(`/api/crm/leads/${id}`);
}

export async function updateLeadStatus(id: string, data: {
  status: CrmLeadStatus;
  notes?: string;
  closedReason?: string;
  closedValue?: number;
  closedNotes?: string;
  nextFollowUp?: string;
  meetingDate?: string;
}): Promise<{ ok: boolean; lead: CrmLead }> {
  return fetchJson(`/api/crm/leads/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function updateCrmLead(id: string, data: Partial<{
  name: string; phone: string; email: string; company: string;
  interest: string; notes: string; nextFollowUp: string; tags: string[];
}>): Promise<{ ok: boolean; lead: CrmLead }> {
  return fetchJson(`/api/crm/leads/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteCrmLead(id: string): Promise<{ ok: boolean; deleted: boolean }> {
  return fetchJson(`/api/crm/leads/${id}`, { method: 'DELETE' });
}

// ===== Lead Lifecycle Endpoints (SRS Addendum) =====

export async function getOverdueLeads(): Promise<{ ok: boolean; leads: CrmLead[]; total: number }> {
  return fetchJson('/api/crm/leads/overdue');
}

export async function getDuplicateLeads(): Promise<{ ok: boolean; leads: CrmLead[]; total: number }> {
  return fetchJson('/api/crm/leads/duplicates');
}

export async function assignLead(leadId: string, data: {
  salesRepId: string; reason?: string;
}): Promise<{ ok: boolean; lead: CrmLead }> {
  return fetchJson(`/api/crm/leads/${leadId}/assign`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function reopenLead(leadId: string, data: {
  status: CrmLeadStatus; notes?: string;
}): Promise<{ ok: boolean; lead: CrmLead }> {
  return fetchJson(`/api/crm/leads/${leadId}/reopen`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function changeLeadCampaign(leadId: string, data: {
  newCampaignId: string; reason?: string;
}): Promise<{ ok: boolean; lead: CrmLead }> {
  return fetchJson(`/api/crm/leads/${leadId}/change-campaign`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function exportLeads(params?: {
  campaign_id?: string; status?: CrmLeadStatus; date_from?: string; date_to?: string;
}): Promise<Blob> {
  const qs = new URLSearchParams();
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null) qs.set(k, String(v));
    }
  }
  const query = qs.toString();
  const resp = await fetch(`/api/crm/leads/export${query ? `?${query}` : ''}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params || {}),
  });
  return resp.blob();
}

export async function resolveDuplicate(leadId: string, data: {
  action: 'dismiss' | 'merge'; mergeIntoId?: string;
}): Promise<{ ok: boolean; lead: CrmLead }> {
  return fetchJson(`/api/crm/leads/${leadId}/resolve-duplicate`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ===== Activities =====

export async function logActivity(data: {
  leadId?: string; activityType: ActivityType; notes?: string; outcome?: string;
  scheduledAt?: string; sourceChannel?: SourceChannel; metadata?: Record<string, any>;
  tcardId?: string; confirmed?: boolean;
  localCaptureTimestamp?: string; deviceId?: string;
}): Promise<{ ok: boolean; activity: LeadActivity }> {
  return fetchJson('/api/crm/activities', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getActivities(params?: {
  lead_id?: string; user_id?: string; activity_type?: ActivityType;
  campaign_id?: string; source_channel?: SourceChannel;
  date_from?: string; date_to?: string;
  page?: number; limit?: number;
}): Promise<{ ok: boolean; activities: LeadActivity[]; total: number; page: number; limit: number }> {
  const qs = new URLSearchParams();
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null) qs.set(k, String(v));
    }
  }
  const query = qs.toString();
  return fetchJson(`/api/crm/activities${query ? `?${query}` : ''}`);
}

export async function getLeadActivities(leadId: string): Promise<{ ok: boolean; activities: LeadActivity[] }> {
  return fetchJson(`/api/crm/activities/lead/${leadId}`);
}

export async function getActivitySummary(params?: {
  date_from?: string; date_to?: string; campaign_id?: string;
}): Promise<{ ok: boolean; kpi: ActivityKPI }> {
  const qs = new URLSearchParams();
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null) qs.set(k, String(v));
    }
  }
  const query = qs.toString();
  return fetchJson(`/api/crm/activities/summary${query ? `?${query}` : ''}`);
}

export async function exportActivities(params?: {
  date_from?: string; date_to?: string; campaign_id?: string; activity_type?: ActivityType;
}): Promise<Blob> {
  const resp = await fetch('/api/crm/activities/export', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params || {}),
  });
  return resp.blob();
}

// ===== Campaigns =====

export async function createCampaign(data: {
  name: string; startDate?: string; endDate?: string; region?: string;
  product?: string; objective?: string; assignedUsers?: string[]; status?: CampaignStatus;
  campaignSlug?: string; multiCampaignPolicy?: string; priorityRank?: number;
}): Promise<{ ok: boolean; campaign: Campaign }> {
  return fetchJson('/api/crm/campaigns', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getCampaigns(params?: { status?: CampaignStatus }): Promise<{ ok: boolean; campaigns: Campaign[] }> {
  const qs = params?.status ? `?status=${params.status}` : '';
  return fetchJson(`/api/crm/campaigns${qs}`);
}

export async function getCampaign(id: string): Promise<{ ok: boolean; campaign: Campaign }> {
  return fetchJson(`/api/crm/campaigns/${id}`);
}

export async function getCampaignAnalytics(id: string): Promise<{ ok: boolean; analytics: CampaignAnalytics }> {
  return fetchJson(`/api/crm/campaigns/${id}/analytics`);
}

export async function updateCampaign(id: string, data: Partial<{
  name: string; startDate: string; endDate: string; region: string;
  product: string; objective: string; status: CampaignStatus; assignedUsers: string[];
  campaignSlug: string; multiCampaignPolicy: string; priorityRank: number;
}>): Promise<{ ok: boolean; campaign: Campaign }> {
  return fetchJson(`/api/crm/campaigns/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function assignCampaignUsers(campaignId: string, userIds: string[]): Promise<{ ok: boolean; assignedUsers: string[] }> {
  return fetchJson(`/api/crm/campaigns/${campaignId}/assign-users`, {
    method: 'POST',
    body: JSON.stringify({ userIds }),
  });
}

export async function exportCampaign(campaignId: string): Promise<Blob> {
  const resp = await fetch(`/api/crm/campaigns/${campaignId}/export`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  return resp.blob();
}

export async function deleteCampaign(id: string): Promise<{ ok: boolean; deleted: boolean }> {
  return fetchJson(`/api/crm/campaigns/${id}`, { method: 'DELETE' });
}

// ===== Analytics & Dashboard =====

export type CrmReportPeriod = 'weekly' | 'monthly' | '3months' | '6months';

function analyticsQs(period?: CrmReportPeriod, type?: string): string {
  const qs = new URLSearchParams();
  if (period) qs.set('period', period);
  if (type) qs.set('type', type);
  const s = qs.toString();
  return s ? `?${s}` : '';
}

export async function getCrmDashboard(period?: CrmReportPeriod): Promise<{ ok: boolean; period?: string; dashboard: CrmDashboardData }> {
  return fetchJson(`/api/crm/analytics/dashboard${analyticsQs(period)}`);
}

export async function getTeamPerformance(period?: CrmReportPeriod): Promise<{ ok: boolean; period?: string; leaderboard: TeamPerformance[] }> {
  return fetchJson(`/api/crm/analytics/team-performance${analyticsQs(period)}`);
}

export async function getPipelineData(period?: CrmReportPeriod): Promise<{ ok: boolean; period?: string; pipeline: PipelineStage[] }> {
  return fetchJson(`/api/crm/analytics/pipeline${analyticsQs(period)}`);
}

// ===== Manager Command Center (Manager Dashboard) =====

export type DashboardPeriod =
  | 'today' | 'yesterday' | 'this_week' | 'this_month' | 'last_month' | 'custom';

export interface KpiDelta {
  direction: 'up' | 'down' | 'flat' | 'none';
  change: number;
  label: string;
}

export interface ScoreComponent {
  key: string;
  label: string;
  result: string;
  points: number;
  max: number;
  applicable: boolean;
}

export interface RepScore {
  repId: string;
  name: string;
  email?: string;
  avatar?: string;
  crmRole?: string;
  rank: number | null;
  score: number | null;
  scoreLevel: string;
  dataSufficient: boolean;
  leads: number;
  qualified: number;
  visits: number;
  won: number;
  revenue: number;
  conversionRate: number | null;
  routeCompletion: number | null;
  followUpRate: number | null;
  components: ScoreComponent[];
  previousScore?: number | null;
  scoreChange?: number | null;
  previousRank?: number | null;
  rankChange?: number | null;
  rankMovement?: 'up' | 'down' | 'flat' | 'new' | 'none';
  onApprovedLeave?: boolean;
  leaveDays?: number;
  assignedTargets?: {
    won: number | null;
    qualified: number | null;
    visits: number | null;
    campaign: number | null;
  } | null;
}

export interface ManagerDashboardData {
  ok: boolean;
  period: string;
  range: { start: string; end: string; label: string };
  asOf: string;
  scope: 'organization' | 'team';
  hasRouteTracking: boolean;
  todaysOperations: {
    activeTeam: { total: number; active: number; inField: number; notStarted: number; delta: KpiDelta };
    routes: { total: number; active: number; completed: number; notStarted: number; delta: KpiDelta };
    visits: { completed: number; planned: number; activeNow: number; completionPct: number; delta: KpiDelta };
    leadsCollected: { total: number; online: number; offline: number; delta: KpiDelta };
    overdueWork: { total: number; overdueVisits: number; overdueFollowUps: number };
    routeCompliance: { pct: number | null; delta: KpiDelta };
  };
  needsAttention: Array<{
    id: string; type: string; severity: 'critical' | 'warning' | 'info';
    title: string; message: string; actionLabel: string; actionTab: string;
  }>;
  leaderboard: RepScore[];
  leadsRequiringFollowUp: {
    overdue: FollowUpLead[]; dueToday: FollowUpLead[]; upcoming: FollowUpLead[]; recentlyAdded: FollowUpLead[];
    counts: { overdue: number; dueToday: number; upcoming: number; recentlyAdded: number };
  };
  activeCampaigns: Array<{
    id: string; name: string; status: string; startDate: string | null; endDate: string | null;
    assignedCount: number; leadsGenerated: number; closedWon: number; conversionRate: number;
    completionPct: number | null; daysRemaining: number | null;
  }>;
  salesPipeline: {
    mainFunnel: Array<{ stage: string; label: string; count: number; pct: number }>;
    lostOutcomes: Array<{ stage: string; label: string; count: number }>;
    totalActive: number; totalAll: number;
  };
  recentActivity: Array<{
    id: string; activityType: string; actorName: string; notes: string | null; outcome: string | null;
    metadata: any; leadId: string | null; campaignId: string | null; sourceChannel: string | null;
    isOffline: boolean; createdAt: string;
  }>;
  locationStatus: { online: number; stale: number; offline: number; tracked: boolean };
}

export interface FollowUpLead {
  id: string; name: string; company: string; owner: string; ownerAvatar: string | null;
  status: CrmLeadStatus; lastActivity: string | null; nextFollowUp: string | null;
  priority: 'high' | 'medium' | 'low'; phone: string | null; warnings: string[];
}

function dashboardQs(period?: DashboardPeriod, from?: string, to?: string, extra?: Record<string, string>): string {
  const qs = new URLSearchParams();
  if (period) qs.set('period', period);
  if (from) qs.set('from', from);
  if (to) qs.set('to', to);
  if (extra) Object.entries(extra).forEach(([k, v]) => { if (v) qs.set(k, v); });
  const s = qs.toString();
  return s ? `?${s}` : '';
}

export async function getManagerDashboard(period?: DashboardPeriod, from?: string, to?: string): Promise<ManagerDashboardData> {
  return fetchJson(`/api/crm/analytics/manager-dashboard${dashboardQs(period, from, to)}`);
}

export async function getCrmLeaderboard(period?: DashboardPeriod, opts?: {
  from?: string; to?: string; limit?: number;
  territoryId?: string; campaignId?: string; routeId?: string;
}): Promise<{ ok: boolean; period: string; range: { start: string; end: string; label: string }; leaderboard: RepScore[]; total: number }> {
  const extra: Record<string, string> = {};
  if (opts?.limit) extra.limit = String(opts.limit);
  if (opts?.territoryId) extra.territoryId = opts.territoryId;
  if (opts?.campaignId) extra.campaignId = opts.campaignId;
  if (opts?.routeId) extra.routeId = opts.routeId;
  return fetchJson(`/api/crm/analytics/leaderboard${dashboardQs(period, opts?.from, opts?.to, Object.keys(extra).length ? extra : undefined)}`);
}

export async function getRepScoreBreakdown(repId: string, period?: DashboardPeriod, from?: string, to?: string): Promise<{ ok: boolean; period: string; rep: RepScore }> {
  return fetchJson(`/api/crm/analytics/rep-score/${encodeURIComponent(repId)}${dashboardQs(period, from, to)}`);
}

// ===== Notifications =====

export async function getCrmNotifications(params?: {
  unread_only?: boolean; page?: number; limit?: number;
}): Promise<{ ok: boolean; notifications: CrmNotification[]; total: number }> {
  const qs = new URLSearchParams();
  if (params?.unread_only) qs.set('unread_only', 'true');
  if (params?.page) qs.set('page', String(params.page));
  if (params?.limit) qs.set('limit', String(params.limit));
  const query = qs.toString();
  return fetchJson(`/api/crm/analytics/notifications${query ? `?${query}` : ''}`);
}

export async function markNotificationRead(id: string): Promise<{ ok: boolean }> {
  return fetchJson(`/api/crm/analytics/notifications/${id}/read`, { method: 'PATCH' });
}

export async function markAllNotificationsRead(): Promise<{ ok: boolean }> {
  return fetchJson('/api/crm/analytics/notifications/mark-all-read', { method: 'POST' });
}

// Aliases used by components
export const editTeamMember = updateTeamMember;
export const approveDelete = approveDeleteMember;
export const createOrganization = createOrUpdateOrganization;
export const getDashboardAnalytics = getCrmDashboard;
export const getPipelineAnalytics = getPipelineData;

// ===== User Search (from existing card owners) =====

export async function searchCrmUsers(q: string): Promise<{ ok: boolean; users: any[] }> {
  return fetchJson(`/api/crm/team/search-users?q=${encodeURIComponent(q)}`);
}

export async function addSalesRepFromUser(data: { userId: string; managerId?: string }): Promise<{ ok: boolean; user: any }> {
  return fetchJson('/api/crm/team/sales-rep/from-user', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function addManagerFromUser(data: { userId: string }): Promise<{ ok: boolean; user: any }> {
  return fetchJson('/api/crm/team/manager/from-user', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ===== Sales Routes =====

export interface RouteStop {
  _id?: string;
  order: number;
  stopType: 'office_start' | 'customer' | 'office_end' | 'custom';
  leadId?: string;
  label: string;
  addressLine?: string;
  latitude?: number;
  longitude?: number;
  expectedArrival?: string;
  priority?: 'low' | 'normal' | 'high';
  tasks?: string[];
  notes?: string;
  assignedUserId?: string;
}

export interface SalesRoute {
  _id: string;
  name: string;
  routeDate: string;
  status: 'draft' | 'published' | 'completed' | 'cancelled';
  assignmentType: 'individual' | 'team';
  assignedUserId?: string;
  assignedUserIds?: string[];
  territoryId?: string;
  stops: RouteStop[];
  complianceScore?: number;
  complianceSummary?: any;
  completedAt?: string;
  deletedAt?: string;
  repName?: string;
  visitedCount?: number;
  totalCustomerStops?: number;
}

export async function listSalesRoutes(params?: {
  date?: string;
  status?: string;
  search?: string;
  repId?: string;
  dateFrom?: string;
  dateTo?: string;
}): Promise<{ routes: SalesRoute[]; count: number }> {
  const qs = new URLSearchParams();
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v) qs.set(k, String(v));
    }
  }
  const query = qs.toString();
  return fetchJson(`/api/crm/routes${query ? `?${query}` : ''}`);
}

export async function listCompletedRoutes(params?: {
  search?: string;
  repId?: string;
  dateFrom?: string;
  dateTo?: string;
}): Promise<{ routes: SalesRoute[]; count: number }> {
  const qs = new URLSearchParams();
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v) qs.set(k, String(v));
    }
  }
  const query = qs.toString();
  return fetchJson(`/api/crm/routes/completed${query ? `?${query}` : ''}`);
}

export async function getRoutePerformance(days = 90): Promise<any> {
  return fetchJson(`/api/crm/routes/performance/reps?days=${days}`);
}

export async function exportRouteReport(routeId: string): Promise<Blob> {
  return fetchBlob(`/api/crm/routes/${routeId}/export`);
}

export async function deleteCompletedRoute(routeId: string): Promise<{ ok: boolean; message?: string }> {
  return fetchJson(`/api/crm/routes/${routeId}`, { method: 'DELETE' });
}

export async function getSalesRoute(id: string): Promise<{ route: SalesRoute }> {
  return fetchJson(`/api/crm/routes/${id}`);
}

export async function createSalesRoute(data: Partial<SalesRoute>): Promise<{ ok: boolean; route: SalesRoute }> {
  return fetchJson('/api/crm/routes', { method: 'POST', body: JSON.stringify(data) });
}

export async function updateSalesRoute(id: string, data: Partial<SalesRoute>): Promise<{ ok: boolean; route: SalesRoute }> {
  return fetchJson(`/api/crm/routes/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
}

export async function publishSalesRoute(id: string): Promise<{ ok: boolean; route: SalesRoute }> {
  return fetchJson(`/api/crm/routes/${id}/publish`, { method: 'POST' });
}

export async function optimizeSalesRoute(id: string): Promise<{ ok: boolean; route: SalesRoute }> {
  return fetchJson(`/api/crm/routes/${id}/optimize`, { method: 'POST' });
}

export async function getRouteCompliance(id: string): Promise<{ report: any }> {
  return fetchJson(`/api/crm/routes/${id}/compliance`);
}

export async function getLiveMapData(): Promise<{
  reps: any[];
  activeRoutes: any[];
  territories?: any[];
  visits?: any[];
  asOf: string;
}> {
  return fetchJson('/api/crm/routes/live/map');
}

export async function getMapsConfig(): Promise<{ enabled: boolean; apiKey: string }> {
  return fetchJson('/api/crm/places/config');
}

export async function placesAutocomplete(q: string): Promise<{ predictions: any[] }> {
  return fetchJson(`/api/crm/places/autocomplete?q=${encodeURIComponent(q)}`);
}

export async function placesDetails(placeId: string): Promise<{ place?: any }> {
  return fetchJson(`/api/crm/places/details?placeId=${encodeURIComponent(placeId)}`);
}

export interface SalesTerritory {
  _id: string;
  name: string;
  description?: string;
  color?: string;
  polygon: { latitude: number; longitude: number }[];
  assignedUserIds?: string[];
}

export async function listTerritories(): Promise<{ territories: SalesTerritory[] }> {
  return fetchJson('/api/crm/territories');
}

export async function createTerritory(data: Partial<SalesTerritory>): Promise<{ ok: boolean; territory: SalesTerritory }> {
  return fetchJson('/api/crm/territories', { method: 'POST', body: JSON.stringify(data) });
}

export async function updateTerritory(id: string, data: Partial<SalesTerritory>): Promise<{ ok: boolean; territory: SalesTerritory }> {
  return fetchJson(`/api/crm/territories/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
}

export async function deleteTerritory(id: string): Promise<{ ok: boolean }> {
  return fetchJson(`/api/crm/territories/${id}`, { method: 'DELETE' });
}

export async function getTeamRouteSummary(date?: string): Promise<any> {
  const qs = date ? `?date=${encodeURIComponent(date)}` : '';
  return fetchJson(`/api/crm/routes/team-summary${qs}`);
}

export async function saveLeadLocation(leadId: string, data: {
  latitude: number; longitude: number; addressLine?: string; location?: string; geofenceRadiusM?: number;
}): Promise<{ ok: boolean; lead: CrmLead }> {
  return fetchJson(`/api/crm/leads/${leadId}/location`, { method: 'PATCH', body: JSON.stringify(data) });
}

// ===== Reports =====

export async function getCrmReports(type?: string, period?: CrmReportPeriod): Promise<{ ok: boolean; role: string; period?: string; reports: any }> {
  return fetchJson(`/api/crm/analytics/reports${analyticsQs(period, type)}`);
}

// ===== Unified Reports (Phase 2 — client + crm_manager analytics center) =====

export type ReportRange = '7d' | '30d' | '3m' | '6m' | 'year' | 'custom';
export type ReportGroupBy = 'daily' | 'weekly' | 'monthly';
export type ReportSection =
  | 'overview' | 'pipeline' | 'conversions' | 'team-performance'
  | 'route-performance' | 'customer-visits' | 'campaigns' | 'lead-sources' | 'activities'
  | 'territory-performance';

export interface ReportOverview {
  kpis: {
    totalLeads: { value: number; delta: KpiDelta };
    qualifiedLeads: { value: number; pctOfTotal: number | null; delta: KpiDelta };
    closedWon: { value: number; revenue: number; delta: KpiDelta };
    conversionRate: { value: number | null; previous: number | null; target: number | null };
    customerVisits: { planned: number; completed: number; missed: number; delta: KpiDelta };
    routeCompletion: { value: number | null; delta: KpiDelta; tracked: boolean };
  };
  leadStatusDistribution: Array<{ status: string; count: number }>;
  leadStatusTotal: number;
  leadsOverTime: Array<{ bucket: string; created: number; qualified: number; won: number; lost: number }>;
  insights: Array<{ id: string; text: string; severity: 'positive' | 'warning' | 'info'; linkTab: string }>;
}

export interface ReportPipelineStage {
  stage: string; label: string; count: number; reached: number; pct: number;
  conversionFromPrev: number | null; dropOff: number | null; avgDaysInStage: number; stalled: number;
}
export interface ReportPipeline {
  mainFunnel: ReportPipelineStage[];
  lostOutcomes: Array<{ stage: string; label: string; count: number }>;
  totalAll: number; totalActive: number; totalStalled: number;
}

export interface ReportConversions {
  total: number; qualified: number; won: number; lost: number;
  overallConversion: number | null; closedLostRate: number | null; avgSalesCycleDays: number | null;
  funnelRates: {
    leadToQualified: number | null; qualifiedToMeeting: number | null;
    meetingToProposal: number | null; proposalToWon: number | null; overall: number | null;
  };
  byRep: Array<{ repId: string; name: string; total: number; qualified: number; won: number; conversionRate: number | null }>;
  bySource: Array<{ source: string; total: number; qualified: number; won: number; conversionRate: number | null }>;
}

export interface ReportTeamPerformance { reps: RepScore[]; orgAvgScore: number | null; }

export interface ReportRouteRep {
  repId: string; name: string; email?: string; avatar?: string;
  completedRoutes: number; avgComplianceScore: number; totalStops: number;
  totalVisited: number; visitRate: number; totalDistanceKm: number; performanceTier: string;
}
export interface ReportRoutePerformance {
  enabled: boolean;
  periodDays?: number; totalCompletedRoutes?: number; orgAvgCompliance?: number;
  reps?: ReportRouteRep[]; topPerformers?: ReportRouteRep[]; needsAttention?: ReportRouteRep[];
}

export interface ReportCustomerVisits {
  metrics: {
    planned: number; completed: number; missed: number; skipped: number; total: number;
    avgDurationMin: number | null; validCheckInRate: number | null; validCheckOutRate: number | null;
    visitsWithNotes: number; visitsProducingLeads: number; suspiciousVisits?: number;
  };
  rows: Array<{
    id: string; customer: string; company: string; rep: string; status: string;
    checkInAt: string | null; checkOutAt: string | null; durationMin: number | null; locationStatus: string;
    verified?: boolean; suspicious?: boolean; distanceM?: number | null; verificationNotes?: string | null;
  }>;
}

export interface ReportTerritoryPerformance {
  enabled?: boolean;
  emptyReason?: string;
  territories: Array<{
    id: string; name: string; color: string; assignedReps: number;
    leads: number; qualified: number; won: number; revenue: number; visits: number;
    conversionRate: number | null;
  }>;
}

export interface ReportCampaigns {
  campaigns: Array<{
    id: string; name: string; status: string; startDate: string | null; endDate: string | null;
    assignedCount: number; leadsGenerated: number; qualified: number; closedWon: number;
    revenue: number; conversionRate: number | null; completionPct: number | null; daysRemaining: number | null;
  }>;
}

export interface ReportLeadSources {
  sources: Array<{ source: string; total: number; qualified: number; won: number; revenue: number; conversionRate: number | null }>;
  knownSources: string[];
}

export interface ReportActivities {
  total: number;
  byType: Array<{ type: string; count: number }>;
  recent: Array<{
    id: string; activityType: string; actorName: string; notes: string | null; outcome: string | null;
    metadata: any; leadId: string | null; campaignId: string | null; isOffline: boolean; createdAt: string;
  }>;
}

export interface ReportSectionResponse<T = any> {
  ok: boolean;
  section: ReportSection;
  range: { range: ReportRange; groupBy: ReportGroupBy; start: string; end: string; label: string };
  data: T;
}

export async function getReportSection<T = any>(
  section: ReportSection,
  opts: {
    range?: ReportRange; groupBy?: ReportGroupBy; from?: string; to?: string;
    territoryId?: string; campaignId?: string; routeId?: string;
  } = {},
): Promise<ReportSectionResponse<T>> {
  const qs = new URLSearchParams();
  qs.set('section', section);
  if (opts.range) qs.set('range', opts.range);
  if (opts.groupBy) qs.set('groupBy', opts.groupBy);
  if (opts.from) qs.set('from', opts.from);
  if (opts.to) qs.set('to', opts.to);
  if (opts.territoryId) qs.set('territoryId', opts.territoryId);
  if (opts.campaignId) qs.set('campaignId', opts.campaignId);
  if (opts.routeId) qs.set('routeId', opts.routeId);
  return fetchJson(`/api/crm/analytics/report-section?${qs.toString()}`);
}

export async function getRepPerformance(userId: string): Promise<{
  ok: boolean; userId: string; name: string;
  performance: {
    targets: { won: number | null; qualified: number | null; visits: number | null; campaign: number | null };
    leavePeriods: Array<{ start: string; end: string; reason?: string }>;
  };
}> {
  return fetchJson(`/api/crm/team/${encodeURIComponent(userId)}/performance`);
}

export async function updateRepPerformance(userId: string, payload: {
  targets?: { won?: number | null; qualified?: number | null; visits?: number | null; campaign?: number | null };
  leavePeriods?: Array<{ start: string; end: string; reason?: string }>;
}): Promise<{ ok: boolean; performance: any }> {
  return fetchJson(`/api/crm/team/${encodeURIComponent(userId)}/performance`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export interface CrmReportPdfPayload {
  role: string;
  period?: CrmReportPeriod;
  activeTab?: string;
  orgName?: string;
  /** UI language: en | sw | fr — PDF labels follow this */
  lang?: string;
  dashData: any;
  teamPerf: any[];
  pipeline: any[];
  reports: any;
}

/** Requests a server-rendered (vector) CRM report PDF and returns the blob. */
export async function downloadCrmReportPdf(payload: CrmReportPdfPayload): Promise<Blob> {
  return fetchBlob('/api/crm/analytics/report-pdf', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

// ===== Audit Logs (CRM-scoped) =====

export async function getCrmAuditLogs(params?: {
  page?: number; limit?: number; action?: string; entity_type?: string;
  category?: string; from?: string; to?: string; actor_email?: string; actor_id?: string;
}): Promise<{ ok: boolean; logs: any[]; total: number; page: number; limit: number }> {
  const qs = new URLSearchParams();
  if (params?.page) qs.set('page', String(params.page));
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.action) qs.set('action', params.action);
  if (params?.entity_type) qs.set('entity_type', params.entity_type);
  if (params?.category) qs.set('category', params.category);
  if (params?.from) qs.set('from', params.from);
  if (params?.to) qs.set('to', params.to);
  if (params?.actor_email) qs.set('actor_email', params.actor_email);
  if (params?.actor_id) qs.set('actor_id', params.actor_id);
  return fetchJson(`/api/crm/analytics/audit-logs?${qs.toString()}`);
}

// ===== Phase 3: saved filters, schedules, excel, manager notes =====

export interface CrmSavedReportFilter {
  id: string; name: string; section: string; range: string; groupBy: string;
  from?: string | null; to?: string | null; isDefault?: boolean; updatedAt?: string;
}

export async function listCrmSavedFilters(): Promise<{ ok: boolean; filters: CrmSavedReportFilter[] }> {
  return fetchJson('/api/crm/analytics/saved-filters');
}

export async function createCrmSavedFilter(payload: Partial<CrmSavedReportFilter>): Promise<{ ok: boolean; filter: CrmSavedReportFilter }> {
  return fetchJson('/api/crm/analytics/saved-filters', { method: 'POST', body: JSON.stringify(payload) });
}

export async function deleteCrmSavedFilter(id: string): Promise<{ ok: boolean }> {
  return fetchJson(`/api/crm/analytics/saved-filters/${id}`, { method: 'DELETE' });
}

export interface CrmReportSchedule {
  id: string; name: string; section: string; range: string; groupBy: string;
  from?: string | null; to?: string | null; frequency: 'daily' | 'weekly';
  weekday?: number; recipients: string[]; format: 'pdf' | 'excel';
  enabled: boolean; lastRunAt?: string | null; lastError?: string | null;
}

export async function listCrmReportSchedules(): Promise<{ ok: boolean; schedules: CrmReportSchedule[] }> {
  return fetchJson('/api/crm/analytics/report-schedules');
}

export async function createCrmReportSchedule(payload: Partial<CrmReportSchedule> & { name: string; recipients: string[] }): Promise<{ ok: boolean; schedule: CrmReportSchedule }> {
  return fetchJson('/api/crm/analytics/report-schedules', { method: 'POST', body: JSON.stringify(payload) });
}

export async function updateCrmReportSchedule(id: string, payload: Partial<CrmReportSchedule>): Promise<{ ok: boolean; schedule: any }> {
  return fetchJson(`/api/crm/analytics/report-schedules/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
}

export async function deleteCrmReportSchedule(id: string): Promise<{ ok: boolean }> {
  return fetchJson(`/api/crm/analytics/report-schedules/${id}`, { method: 'DELETE' });
}

export async function downloadCrmReportExcel(payload: {
  range?: string; groupBy?: string; from?: string; to?: string; sections?: string[];
}): Promise<Blob> {
  return fetchBlob('/api/crm/analytics/report-excel', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export interface CrmManagerNote {
  id: string; body: string; periodKey?: string | null; createdAt: string;
  authorName: string; authorId: string;
}

export async function getRepManagerNotes(repId: string): Promise<{ ok: boolean; notes: CrmManagerNote[] }> {
  return fetchJson(`/api/crm/reps/${repId}/manager-notes`);
}

export async function addRepManagerNote(repId: string, body: string, periodKey?: string): Promise<{ ok: boolean; note: CrmManagerNote }> {
  return fetchJson(`/api/crm/reps/${repId}/manager-notes`, {
    method: 'POST',
    body: JSON.stringify({ body, periodKey }),
  });
}
